import { Typography, TableRow, TableCell, LinearProgress } from "@mui/material";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import Btn from "./Btn";
import DynamicSkillSelector from "./DynamicSkillSelector";
import angelWingsIcon from "../assets/svg/angel-wings.svg";

const WINGS_STYLE = { width: 10, filter: "brightness(0) invert(1)", opacity: 0.8, verticalAlign: "middle" };

export default function MjPlayerTableRow({
  char, gauges, draconique, celeste,
  isKO, isDead,
  selected, toggleSelected, currentTurnCharId,
  selectorVisibleFor, setSelectorVisibleFor,
  handleFinalSelection, skillScores,
  openAlertDialogFor,
  theme,
}) {
  const n = (v) => Number(v);
  const clamp = (v) => Math.min(100, Math.max(0, v));

  const currentManaVital   = n(gauges?.currentManaVital   ?? char.ManaVital_character);
  const currentStamina     = n(gauges?.currentStamina     ?? char.Stamina_character);
  const currentManaAir     = n(gauges?.currentManaAir     ?? char.ManaAir_character);
  const currentManaEau     = n(gauges?.currentManaEau     ?? char.ManaEau_character);
  const currentManaTerre   = n(gauges?.currentManaTerre   ?? char.ManaTerre_character);
  const currentManaFeu     = n(gauges?.currentManaFeu     ?? char.ManaFeu_character);
  const currentManaVolonte = n(gauges?.currentManaVolonte ?? char.ManaVolonte_character);

  const clampedManaVital   = clamp((currentManaVital   / char.ManaVital_character)   * 100);
  const clampedStamina     = clamp((currentStamina     / char.Stamina_character)     * 100);
  const clampedManaAir     = clamp((currentManaAir     / char.ManaAir_character)     * 100);
  const clampedManaEau     = clamp((currentManaEau     / char.ManaEau_character)     * 100);
  const clampedManaTerre   = clamp((currentManaTerre   / char.ManaTerre_character)   * 100);
  const clampedManaFeu     = clamp((currentManaFeu     / char.ManaFeu_character)     * 100);
  const clampedManaVolonte = clamp((currentManaVolonte / char.ManaVolonte_character) * 100);

  const dead = isDead(char);
  const ko   = isKO(char);
  const isCurrentTurn = currentTurnCharId === char.ID_character;

  const celestePct = (field) => celeste ? Number(celeste[field]) : 0;

  const Wings = ({ field }) => {
    const pct = celestePct(field);
    if (!pct) return null;
    return (
      <span style={{ display: "block", textAlign: "center", whiteSpace: "nowrap", marginTop: 2, fontSize: "0.7rem" }}>
        <img src={angelWingsIcon} style={WINGS_STYLE} alt="" />
        {" "}{pct}%{" "}
        <img src={angelWingsIcon} style={{ ...WINGS_STYLE, transform: "scaleX(-1)" }} alt="" />
      </span>
    );
  };

  // Vertical bar in a fixed-height container so it doesn't disturb the flex flow
  const VertBar = ({ value, sx }) => (
    <div style={{ height: 64, display: "flex", alignItems: "center", justifyContent: "center", overflow: "visible" }}>
      <LinearProgress variant="determinate" value={value}
        sx={{ height: 5, width: 60, borderRadius: 25, transform: "rotate(-90deg)", ...sx }} />
    </div>
  );

  // Cell content for Air / Eau / Terre / Feu: normal bar + optional draconique bar
  const DoubleMana = ({ normalValue, normalClamped, normalBarSx, celesteField, dracCurrentKey, dracMaxKey }) => {
    const dracCurr = draconique ? n(draconique[dracCurrentKey] ?? 0) : 0;
    const dracMax  = draconique ? n(draconique[dracMaxKey]    ?? 1) : 1;
    const dracPct  = clamp((dracCurr / Math.max(1, dracMax)) * 100);
    return (
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        {/* Jauge normale */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <VertBar value={normalClamped} sx={normalBarSx} />
          <Typography variant="h5" sx={{ color: theme.custom.mymodal.text }} textAlign="center">
            {normalValue}
          </Typography>
          <Wings field={celesteField} />
        </div>
        {/* Jauge draconique — mêmes couleurs que la jauge normale */}
        {draconique && (
          <>
            <div style={{ width: 1, background: "rgba(255,255,255,0.15)", alignSelf: "stretch" }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <VertBar value={dracPct} sx={normalBarSx} />
              <Typography variant="h5" sx={{ color: theme.custom.mymodal.text }} textAlign="center">
                {dracCurr}/{dracMax}
              </Typography>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <TableRow
      key={char.ID_character}
      sx={{
        border: "none", position: "relative",
        opacity: ko && !dead ? 0.35 : 1,
        outline: isCurrentTurn ? "2px solid #ffa726" : "none",
        background: isCurrentTurn ? "rgba(255,167,38,0.1)" : "transparent",
        transition: "background 0.3s",
        ...(dead && {
          "&::after": {
            content: '"💀  MORT"',
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(0,0,0,0.72)", color: "#f87171",
            fontWeight: "bold", letterSpacing: "0.35em", fontSize: "1rem",
            zIndex: 2, pointerEvents: "none",
          },
        }),
      }}
    >
      <TableCell sx={{ border: "none" }}>{char.Name_character}</TableCell>

      {/* Mana Vital — pas draconique */}
      <TableCell sx={{ border: "none" }}>
        <div className="container" style={{ width: "120px", height: "60px", overflow: "hidden", margin: "0 auto" }}>
          <CircularProgressbar value={clampedManaVital} circleRatio={0.5}
            styles={buildStyles({ rotation: 0.75, strokeLinecap: "butt", pathTransitionDuration: 0.5, pathColor: "red", textColor: "#f88", trailColor: "#af8c8d", backgroundColor: "#3e98c7" })} />
        </div>
        <Typography className="label" variant="h5" sx={{ color: theme.custom.mymodal.text }} textAlign="center">
          {currentManaVital}
          {celestePct("CelesteManaVital") > 0 && <Wings field="CelesteManaVital" />}
        </Typography>
      </TableCell>

      {/* Stamina — pas draconique */}
      <TableCell sx={{ border: "none" }}>
        <div className="container" style={{ width: "120px", height: "60px", overflow: "hidden", margin: "0 auto" }}>
          <CircularProgressbar value={clampedStamina} circleRatio={0.5}
            styles={buildStyles({ rotation: 0.75, strokeLinecap: "butt", pathTransitionDuration: 0.5, pathColor: "#4caf50", textColor: "#f88", trailColor: "#cfe9d2", backgroundColor: "#3e98c7" })} />
        </div>
        <Typography className="label" variant="h5" sx={{ color: theme.custom.mymodal.text }} textAlign="center">
          {currentStamina}
        </Typography>
      </TableCell>

      {/* Air — doublé si draconique */}
      <TableCell sx={{ border: "none" }}>
        <DoubleMana
          normalValue={currentManaAir} normalClamped={clampedManaAir}
          normalBarSx={{ "& .MuiLinearProgress-bar": { backgroundColor: "#14b8a6" } }}
          celesteField="CelesteManaAir"
          dracCurrentKey="CurrentManaAirDraconique" dracMaxKey="ManaAirDraconiqueMax"
        />
      </TableCell>

      {/* Eau — doublé si draconique */}
      <TableCell sx={{ border: "none" }}>
        <DoubleMana
          normalValue={currentManaEau} normalClamped={clampedManaEau}
          normalBarSx={{}}
          celesteField="CelesteManaEau"
          dracCurrentKey="CurrentManaEauDraconique" dracMaxKey="ManaEauDraconiqueMax"
        />
      </TableCell>

      {/* Terre — doublé si draconique */}
      <TableCell sx={{ border: "none" }}>
        <DoubleMana
          normalValue={currentManaTerre} normalClamped={clampedManaTerre}
          normalBarSx={{}}
          celesteField="CelesteManaTerre"
          dracCurrentKey="CurrentManaTerreDraconique" dracMaxKey="ManaTerreDraconiqueMax"
        />
      </TableCell>

      {/* Feu — doublé si draconique */}
      <TableCell sx={{ border: "none" }}>
        <DoubleMana
          normalValue={currentManaFeu} normalClamped={clampedManaFeu}
          normalBarSx={{ "& .MuiLinearProgress-bar": { backgroundColor: "#fb7185" }, backgroundColor: "#991b1b" }}
          celesteField="CelesteManafeu"
          dracCurrentKey="CurrentManaFeuDraconique" dracMaxKey="ManaFeuDraconiqueMax"
        />
      </TableCell>

      {/* Volonté — pas draconique */}
      <TableCell sx={{ border: "none" }}>
        <VertBar value={clampedManaVolonte} sx={{
          "& .MuiLinearProgress-bar": { backgroundColor: "#a855f7" },
          backgroundColor: "#6b21a8",
        }} />
        <Typography variant="h5" sx={{ color: theme.custom.mymodal.text }} textAlign="center">
          {currentManaVolonte}
          {celestePct("CelesteManaVolonte") > 0 && <Wings field="CelesteManaVolonte" />}
        </Typography>
      </TableCell>

      {/* Actions compétence */}
      <TableCell sx={{ border: "none" }}>
        <Btn onClick={() => setSelectorVisibleFor(selectorVisibleFor === char.ID_character ? null : char.ID_character)} msg="Compétence" />
        {selectorVisibleFor === char.ID_character && (
          <DynamicSkillSelector onFinalSelect={(path) => handleFinalSelection(char.ID_character, path)} />
        )}
        {skillScores[char.ID_character] && (
          <h2 style={{ marginTop: "8px", fontSize: "1.2rem", color: "white" }}>
            {skillScores[char.ID_character].label} : {skillScores[char.ID_character].valeur}
          </h2>
        )}
      </TableCell>

      <TableCell sx={{ border: "none" }}>
        <input type="checkbox" checked={selected.has(char.ID_character)} onChange={() => toggleSelected(char.ID_character)} />
      </TableCell>

      <TableCell sx={{ border: "none" }}>
        <Btn msg="Envoyer" onClick={() => openAlertDialogFor([char.ID_character])} />
      </TableCell>
    </TableRow>
  );
}
